import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import { apiClient } from 'modules/core/lib/mobx-rest'

import { CellCoordinates } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/EditionStep/editableContentReducer'
import { WizardData } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/WizardData'
import { MappedFields } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/types'

import useGetDateFormat from './useGetDateFormat'

const createInBulkEndpoint = '/employees/create_in_bulk'

export interface EmployeeFieldValue {
  field_name: string
  value: string | number
}

interface Props {
  wizardData: WizardData
  rowsWithError: (number | false)[] | undefined
  ignoredRows: number[]
  cellCoordinatesWithWarnings: CellCoordinates[] | undefined
}

const getCellValue = (
  fieldName: string,
  fieldType: MappedFields['fieldType'],
  cell: string,
  dateFormat: string
) => {
  if (fieldName === 'nationality' || fieldName === 'country') return cell

  if (fieldType === 'enum' || fieldType === 'integer') {
    if (fieldName === 'salary_amount_in_cents' || fieldName === 'contracts_working_hours') {
      return Math.round(Number(cell) * 100)
    }

    return Number(cell)
  }

  if (fieldType === 'date') {
    return dateToString(moment(cell, dateFormat), 'backend')
  }

  return cell
}

const params = (
  wizardData: WizardData,
  dateFormat: string,
  rowsWithError: (number | false)[] | undefined,
  ignoredRows: number[],
  cellCoordinatesWithWarnings: CellCoordinates[] | undefined
) => {
  const employeesData = wizardData.editedFileContent?.rows
    ?.map((row, i) => {
      if (rowsWithError?.includes(i)) return
      if (ignoredRows.includes(i)) return

      return row.reduce((acc, cell, index) => {
        const field = wizardData.mappedFields.find((x) => x.columnIndex === index)

        let fieldName = field?.fieldName || ''
        const fieldValue = getCellValue(fieldName, field?.fieldType, cell, dateFormat)
        if (fieldName === 'contract_start_date') {
          fieldName = 'contract_starts_on'
        }

        if (
          field &&
          !cellCoordinatesWithWarnings?.find((x) => x.rowIndex === i && x.columnIndex === index)
        ) {
          acc?.push({
            field_name: fieldName,
            value: fieldValue,
          })

          if (fieldName === 'contracts_working_hours') {
            acc?.push({
              field_name: 'contracts_working_hours_frequency',
              value: 'week',
            })
          }

          if (fieldName === 'salary_amount_in_cents') {
            acc?.push({
              field_name: 'salary_frequency',
              value: 'yearly',
            })
          }

          if (fieldName === 'contract_starts_on') {
            acc?.push({
              field_name: 'contract_effective_on',
              value: fieldValue,
            })
          }
        }

        return acc
        // eslint-disable-next-line no-type-assertion/no-type-assertion
      }, [] as EmployeeFieldValue[])
    })
    .filter((row) => row !== undefined && row !== null)

  return {
    importer_process_uuid: wizardData.importerProcessUuid,
    employees: employeesData,
  }
}

export const useCreateEmployeesInBulk = () => {
  const { dateFormat } = useGetDateFormat()
  const createEmployeesInBulk = async ({
    wizardData,
    rowsWithError,
    ignoredRows,
    cellCoordinatesWithWarnings,
  }: Props) => {
    const api = apiClient()

    return await api.post(
      createInBulkEndpoint,
      params(wizardData, dateFormat, rowsWithError, ignoredRows, cellCoordinatesWithWarnings)
    )
  }

  return {
    createEmployeesInBulk,
  }
}
